var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { SRGBColorSpace, Texture } from "three";
import { Rectangle } from "../../math";
import { BaseImageryProvider } from "./BaseImageryProvider";
import { WebMercatorTilingScheme } from "../../math/tiling";
/**
 * Provider for an ordered set of imagery layers
 */
export class LayeredImageryProvider extends BaseImageryProvider {
    constructor(layers) {
        super();
        // TODO: This requires some thinking still. We may need to handle layer sets with more than one tiling scheme.
        //       e.g. 2 layers, one geographic and one web mercator. An imagery provider can only have a single tiling
        //       scheme, so what do we return here? We need a way to map between the tiling schemes, and a process to
        //       choose the most compatible option.
        this._tilingScheme = new WebMercatorTilingScheme();
        this.layers = [...layers];
        // Currently we only support web mercator so we should error if we have anything else
        this.layers.forEach((v) => {
            if (!(v.tilingScheme instanceof WebMercatorTilingScheme)) {
                throw new Error("Currently we only support 'WebMercatorTilingScheme' for imagery");
            }
        });
        // The globally minimum zoom level of the combined dataset is simply the lowest zoom level of any layer
        this._minLevel = Math.min(...this.layers.map((v) => v.minLevel));
        // The globally maximum zoom level of the combined dataset is simply the highest zoom level of any layer
        this._maxLevel = Math.max(...this.layers.map((v) => v.maxLevel));
        // Find the union of all layers
        const layersRegion = this.layers
            .map((v) => v.region)
            .reduce((p, c, i) => {
            return p.union(c, p);
        }, new Rectangle());
        // Constrain the region to be within the tiling scheme
        const region = layersRegion.intersection(this._tilingScheme.rectangle);
        // If the output region is not legal it is very likely that
        if (!region.isLegal())
            throw new Error("Layered region is not legal, invalid layer rectangles received");
        this._origin = region.midpoint;
        this._region = region;
    }
    get attributions() {
        return this.layers.flatMap((v) => v.attributions);
    }
    get minLevel() {
        return this._minLevel;
    }
    get maxLevel() {
        return this._maxLevel;
    }
    get tilingScheme() {
        return this._tilingScheme;
    }
    get origin() {
        return this._origin;
    }
    get region() {
        return this._region;
    }
    splitImagery(tile, x, y, zoomLevel) {
        const zoomDifference = zoomLevel - tile.zoomLevel;
        const scale = Math.pow(2, zoomDifference);
        const step = 1 / scale;
        const image = tile.texture.image;
        const canvas = document.createElement("canvas");
        canvas.width = image.width;
        canvas.height = image.height;
        const context = canvas.getContext("2d");
        if (!context) {
            throw new Error("Unable to create HTML canvas.");
        }
        // Determine the coordinate of the tile belongs to
        const xOffset = x - scale * tile.x;
        const yOffset = y - scale * tile.y;
        // Draw the subimage onto the canvas
        context.drawImage(image, image.width * step * xOffset, image.height * step * yOffset, image.width * step, image.height * step, 0, 0, canvas.width, canvas.height);
        // Create the tile texture
        const texture = new Texture();
        // @ts-ignore
        texture.colorSpace = SRGBColorSpace;
        texture.image = canvas;
        texture.needsUpdate = true;
        return {
            texture,
            x,
            y,
            zoomLevel,
        };
    }
    loadTile(x, y, zoomLevel) {
        return __awaiter(this, void 0, void 0, function* () {
            // Start a load request for all tiles concurrently.
            const promises = [];
            for (const layer of this.layers) {
                let realZoom = zoomLevel;
                let realX = x;
                let realY = y;
                // If we're trying to load data beyond the max zoom level of the dataset we clamp to the highest available
                // zoom level.
                const maxLevel = layer.maxLevel;
                if (zoomLevel > maxLevel) {
                    // Clamp the zoom level to the maximum available in the tileset
                    realZoom = maxLevel;
                    // Calculate the tile at the more coarse zoom level that contains the data for the more refined tile
                    // we requested.
                    const zoomDifference = zoomLevel - maxLevel;
                    const zoomDivisor = Math.pow(2, zoomDifference);
                    realX = Math.trunc(x / zoomDivisor);
                    realY = Math.trunc(y / zoomDivisor);
                }
                promises.push(layer.loadTile(realX, realY, realZoom));
            }
            // Wait for all the promises to complete, successfully or otherwise
            const results = yield Promise.allSettled(promises);
            // Flatten all result arrays into a single array and return it
            const out = [];
            for (const result of results) {
                if (result.status === "fulfilled") {
                    for (const tile of result.value) {
                        if (tile.zoomLevel !== zoomLevel) {
                            out.push(this.splitImagery(tile, x, y, zoomLevel));
                        }
                        else {
                            out.push(tile);
                        }
                    }
                }
            }
            return out;
        });
    }
    hasDataForTile(x, y, zoomLevel) {
        // Only a single layer has to provide data
        for (const layer of this.layers) {
            if (layer.hasDataForTile(x, y, zoomLevel)) {
                return true;
            }
        }
        return false;
    }
    canRefine(x, y, zoomLevel) {
        // Only a single layer must have refinement available
        for (const layer of this.layers) {
            if (layer.canRefine(x, y, zoomLevel)) {
                return true;
            }
        }
        return false;
    }
    debugDraw(worldTransform) {
        for (const layer of this.layers) {
            if (typeof layer.debugDraw !== "undefined") {
                layer.debugDraw(worldTransform);
            }
        }
    }
}
